/**
 * Created by fers on 26.09.15.
 */

import { v4 as uuidv4 } from 'uuid';
import { declareEntity } from '../fw/MetaDataHelper.ts';
import * as Permission from '../util/Permission';
import {channelDefState} from './ChannelDefType';
import {RequiredPayPlans} from './OrganisationType';
import config from '../../common/config';

export default declareEntity({
    entity: "scenarioDefs",
    graphQL: "ScenarioDef",
    name: "Scenario Definition",
    writePermission: [
        Permission.PERM_EDIT_CHANNEL_DEF
    ],
    dynamoPrefix: 'def',
    storageType: 'schema',
    defaultTitle: (data) => {
        return data['name'];
    },
    isPublic: true,
    defaultOrder: ['name'],
    fields: (types) => { return {
        id: {
            name: "Scenario Def ID",
            type: "id",
            defaultValue: () => {
                return uuidv4();
            },
            primaryKey: true,
            required: true,
            userEditable: false,
            isPublic: true,
        },
        name: {
            name: "Name",
            description: "Name of Scenario Definition",
            trim: true,
            type: "string",
            translatable: true,
            filterable: true,
            defaultValue: "",
            isPublic: true,
        },
        title: {
            name: "Title",
            description: "Title used in scenarios.",
            trim: true,
            type: "string",
            translatable: true,
            defaultValue: "",
            isPublic: true,
        },
        urlFragment: {
            name: "URL Fragment",
            trim: true,
            type: "string",
            description: "URL is used to generate scenario URLs so {key} must be used.",
            required: true,
            defaultValue: "",
            validations: {
                matchRegexp: /^[a-zA-Z0-9\-_\{\}]+$/,
            },
            filterable: true,
            isPublic: true,
        },
        urlFragmentRaw: {
            name: "URL Fragment",
            type: "string",
            defaultValue: "",
            filterable: true,
            isPublic: true,
            userEditable: false,
        },
        version: {
            name: "Definition version",
            type: "int",
            defaultValue: 1,
        },
        versionConfig: {
            name: "Version Config",
            type: "object",
            import: false,
            export: false,
            userEditable: false,
            permission: Permission.PERM_DEVELOPER,
        },
        hasChange: {
            name: "Has Change",
            type: "boolean",
            filterable: true,
            export: false,
            import: false,
            userEditable: false,
            isPublic: false,
            permission: Permission.PERM_DEVELOPER,
        },
        originalScenarioDefVersion: {
            name: "Original Scenario Definition Version",
            to: () => types['scenarioDefVersions'],
            type: "relation",
            export: false,
            userEditable: false,
            permission: Permission.PERM_DEVELOPER,
        },
        popularity: {
            name: "Popularity",
            type: "int",
            defaultValue: 1,
            isPublic: true,
            import: false,
            export: false,
        },
        deprecated: {
            name: "Is Deprecated?",
            type: "boolean",
            defaultValue: false,
            filterable: true,
            isPublic: true,
        },
        showContactUs: {
            name: "Show Contact Us",
            type: "boolean",
            defaultValue: false,
            isPublic: true,
        },
        deprecatedBy: {
            name: "Deprecated by",
            type: "relation",
            to: () => types['scenarioDefs'],
            visibility: (data) => { return data['deprecated']; },
        },
        description: {
            name: "Description",
            type: "text",
            description: "Description of Scenario so {key} should be used.",
            translatable: true,
            defaultValue: "",
            isPublic: true,
        },
        requiredPayPlan: {
            name: "Required Plan",
            type: "enum",
            isPublic: true,
            values: RequiredPayPlans,
        },
        overridePayPlan: {
            name: "Override Required Plan",
            type: "boolean",
            isPublic: true,
            defaultValue: true,
        },
        longDescription: {
            name: "Long Description",
            type: "html",
            description: "Long Description of Scenario so {key} should be used.",
            translatable: true,
            defaultValue: "",
            isPublic: true,
        },
        state: {
            name: "State",
            type: "enum",
            filterable: true,
            defaultValue: "draft",
            values: channelDefState,
            isPublic: true,
        },
        visibility: {
            name: "Visibility",
            type: "enum",
            filterable: true,
            defaultValue: "private",
            isPublic: true,
            values: {
                "private": {
                    name: "Private",
                },
                "unlisted": {
                    name: "Unlisted",
                },
                "public": {
                    name: "Public",
                },
            }
        },
        developerGroup: {
            name: "Developer",
            type: "relation",
            to: () => types['developerGroups'],
            userEditable: true,
            defaultValue: config.DEVELOPER_DATIVERY,
            filterable: true,
            required: true,
            isPublic: true,
        },
        specializationOf: {
            name: "Specialization of",
            type: "relation",
            to: () => types['scenarioDefs'],
        },
        author: {
            name: "Author",
            type: "relation",
            to: () => types['users'],
            userEditable: false,
        },
        changeAuthor: {
            name: "Last change author",
            type: "relation",
            to: () => types['users'],
            userEditable: false,
        },
        changeAuthorName: {
            name: "Change Author Name",
            type: "string",
            userEditable: false,
        },
        updatedAt: {
            name: "Updated At",
            type: "datetime",
            defaultValue: () => {
                return new Date();
            },
            userEditable: false,
        },
        createdAt: {
            name: "Created At",
            type: "datetime",
            defaultValue: () => {
                return new Date();
            },
            userEditable: false,
        },
    }; },
    relations: (types) => { return {
        channelDefRules: {
            type: 'oneToMany',
            name: "Application Definition Rules",
            to: types['scenarioChannelDefRules'],
            export: true,
            defaultValue: () => {
                return {};
            },
            isPublic: true,
        },
        screenDefs: {
            type: 'oneToMany',
            name: "ScreenDefs",
            to: types['scenarioScreenDefs'],
            export: true,
            defaultValue: () => {
                return {};
            },
            isPublic: true,
        },
        specializations: {
            type: 'oneToMany',
            to: types['scenarioDefs'],
            foreignKeys: [
                {
                    from: "id",
                    to: "specializationOf_id"
                }
            ],
            isPublic: true,
        },
        scenarioDefVersions: {
            type: 'oneToMany',
            to: types['scenarioDefVersions'],
            export: false,
            permission: Permission.PERM_INSTANCE_ADMIN,
            foreignKeys: [
                {
                    from: "id",
                    to: "scenarioDef_id"
                }
            ]
        },
        scenarioDefs2ChannelDefs: {
            type: 'oneToMany',
            to: types['scenarioDefs2ChannelDefs'],
            export: true,
            foreignKeys: [
                {
                    from: "id",
                    to: "scenarioDef_id"
                }
            ],
            isPublic: true,
        },
        scenarios2ChannelDefs: {
            type: 'oneToMany',
            to: types['scenarios2ChannelDefs'],
            foreignKeys: [
                {
                    from: "id",
                    to: "scenarioDef_id"
                }
            ],
            isPublic: true,
        },
        scenarios: {
            type: 'oneToMany',
            to: types['scenarios'],
            export: true,
            foreignKeys: [
                {
                    from: "id",
                    to: "scenarioDef_id"
                }
            ],
            isPublic: true,
        },
        scenarioFlowDefs: {
            type: 'oneToMany',
            to: types['scenarioFlowDefs'],
            export: true,
            foreignKeys: [
                {
                    from: "id",
                    to: "scenarioDef_id"
                }
            ],
            isPublic: true,
        },
        relevantScenarios: {
            type: 'oneToMany',
            to: types['scenarios'],
            isPublic: true,
            args: {
                onlyActiveChannels: {
                    type: 'boolean',
                },
                first: {
                    type: 'int'
                },
                requiredPayPlan: {
                    type: 'string'
                }
            },
            controllerFn: 'getRelevantScenarios',
        },
    }; }
});
